import { HeaderPositionType, ListStyleType, TextStyleType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class BeforeAfterCategory extends BaseCategory {
    get name() {
        return "Before and After";
    }

    get type() {
        return SlideCategories.BEFORE_AFTER;
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use for showing a comparison between a before state and an after state, such as process improvements, transformations, or changes over time.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Create a before and after comparison for the slide based on the user's prompt.
        Provide a list of items for the "before" state and corresponding items for the "after" state.
        Each item should be a brief description of the state or condition.
        Optionally, create a conclusion or takeaway for the slide.`;
    }

    getModelHints() {
        return {
            slideType: "before and after comparison",
        };
    }

    getPromptSchema() {
        return {
            beforeLabel: "optional custom label for the before column (defaults to 'Before')",
            afterLabel: "optional custom label for the after column (defaults to 'After')",
            beforeItems: ["array of bullets describing the before state"],
            afterItems: ["array of bullets describing the after state"],
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    getSlideModels(model, options = {}) {
        let models = [
            this.buildBeforeAfterSlide(model, options),
            this.buildBeforeAfterTextBoxGrid(model, options)
        ];
        return models;
    }

    buildBeforeAfterSlide(model) {
        let builder = new SlideModelBuilder("textlist", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        builder.createPrimary({
            collectionColor: "theme",
            listStyle: ListStyleType.BULLET,
            showColumnHeaders: true,
            columnHeader0: {
                blocks: [
                    builder.createBlock(model.beforeLabel || "Before", TextStyleType.TITLE, { listStyle: ListStyleType.TEXT })
                ]
            },
            columnHeader1: {
                blocks: [
                    builder.createBlock(model.afterLabel || "After", TextStyleType.TITLE, { listStyle: ListStyleType.TEXT })
                ]
            },
            items: [{
                isMedia: false,
                text: {
                    blocks: model.beforeItems.map(item => builder.createBlock(item, TextStyleType.BULLET_LIST, { listStyle: ListStyleType.BULLET }))
                }
            }, {
                isMedia: false,
                text: {
                    blocks: model.afterItems.map(item => builder.createBlock(item, TextStyleType.BULLET_LIST, { listStyle: ListStyleType.BULLET }))
                }
            }]
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }

    buildBeforeAfterTextBoxGrid(model) {
        let builder = new SlideModelBuilder("textBoxGrid", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        builder.createPrimary({
            collectionColor: "theme",
            flipOrientation: false,
            fitToText: false,
            items: [{
                color: "theme",
                text: {
                    blocks: [
                        builder.createBlock(model.beforeLabel || "Before", TextStyleType.TITLE),
                        ...model.beforeItems.map(item => builder.createBlock(item, TextStyleType.BULLET_LIST, { listStyle: ListStyleType.BULLET }))
                    ]
                }
            }, {
                color: "accent1",
                text: {
                    blocks: [
                        builder.createBlock(model.afterLabel || "After", TextStyleType.TITLE),
                        ...model.afterItems.map(item => builder.createBlock(item, TextStyleType.BULLET_LIST, { listStyle: ListStyleType.BULLET }))
                    ]
                }
            }]
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }
}
